import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  type Auth,
} from "firebase/auth";
import { getFirestore, type Firestore } from "firebase/firestore";

/**
 * Fill via `.env` (see `.env.example`) after you create the Firebase project.
 * Until then the app runs in guest / local-profile mode.
 */
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string | undefined,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string | undefined,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET as string | undefined,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string | undefined,
  appId: import.meta.env.VITE_FIREBASE_APP_ID as string | undefined,
};

export const isFirebaseConfigured = Boolean(
  firebaseConfig.apiKey &&
    firebaseConfig.authDomain &&
    firebaseConfig.projectId &&
    firebaseConfig.appId,
);

/** Public R2 base URL you will paste after creating the bucket (no trailing slash). */
export const premiumCdnBase = (
  (import.meta.env.VITE_PREMIUM_CDN_BASE as string | undefined) || ""
).replace(/\/$/, "");

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let db: Firestore | null = null;

export function getFirebaseApp(): FirebaseApp | null {
  if (!isFirebaseConfigured) return null;
  if (!app) {
    app = initializeApp(firebaseConfig as Required<typeof firebaseConfig>);
  }
  return app;
}

/**
 * Explicit multi-layer persistence so refresh / relaunch keeps Google & guest sessions
 * (web, Electron localhost, Capacitor WebView).
 */
export function getFirebaseAuth(): Auth | null {
  const a = getFirebaseApp();
  if (!a) return null;
  if (!auth) {
    try {
      auth = initializeAuth(a, {
        persistence: [indexedDBLocalPersistence, browserLocalPersistence, browserSessionPersistence],
      });
    } catch {
      // Hot reload / second getAuth — already initialized
      auth = getAuth(a);
    }
  }
  return auth;
}

export function getDb(): Firestore | null {
  const a = getFirebaseApp();
  if (!a) return null;
  // Initialize Auth first so explicit persistence wins (Firestore must not precede Auth).
  getFirebaseAuth();
  if (!db) db = getFirestore(a);
  return db;
}
